import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';

import Button, { BUTTON_KIND } from '@/components/Button';
import { InfoPanelContainer } from '@/components/InfoPanelContainer';
import { FormInput } from '@/components/Input';
import { ExtraEmail, ExtraMobileNumber } from '@/types/IProfile';

import { AdditionalContactInfoForm } from './schema';

export function AdditionalContactInfo({
  disabled = false,
}: {
  disabled?: boolean;
}) {
  const { watch } = useFormContext<AdditionalContactInfoForm>();

  const additionalContactInfo = watch('additional_information') || [];
  const { append: addInfo } = useFieldArray({
    name: 'additional_information',
  });

  return (
    <InfoPanelContainer>
      <div className="text-base font-medium text-gray-900 capitalize pb-6 border-b border-gray-200">
        Additional information
      </div>
      <div className="flex flex-col space-y-3 mt-6">
        {additionalContactInfo.map((contact, index) => (
          <EditAdditionalContactInfo
            key={index}
            index={index}
            contact={contact}
            isLast={index === additionalContactInfo.length - 1}
            disabled={disabled}
          />
        ))}
        <div className="flex flex-wrap gap-3">
          <Button
            buttonText="Add phone number"
            icon={<PhoneIcon />}
            kind={BUTTON_KIND.WHITE}
            onClick={() => {
              addInfo({ type: 'phone', label: '', value: '' });
            }}
            disabled={disabled}
          />
          <Button
            buttonText="Add email address"
            icon={<EnvelopeIcon />}
            kind={BUTTON_KIND.WHITE}
            onClick={() => {
              addInfo({ type: 'email', label: '', value: '' });
            }}
            disabled={disabled}
          />
        </div>
      </div>
    </InfoPanelContainer>
  );
}

type ExtraContactInfoProp = ExtraEmail | ExtraMobileNumber;

type EditAdditionalContactInfoProps = {
  contact: ExtraContactInfoProp;
  index: number;
  isLast: boolean;
  disabled?: boolean;
};

function EditAdditionalContactInfo({
  contact,
  index,
  isLast,
  disabled,
}: EditAdditionalContactInfoProps) {
  const {
    getValues,
    setValue,
    register,
    formState: { errors },
  } = useFormContext<AdditionalContactInfoForm>();

  const { remove: removeLink } = useFieldArray({
    name: 'additional_information',
  });

  function moveLink(direction: 'up' | 'down') {
    const additionalInfo = getValues('additional_information');

    if (direction === 'up') {
      [additionalInfo[index - 1], additionalInfo[index]] = [
        additionalInfo[index],
        additionalInfo[index - 1],
      ];
    } else {
      [additionalInfo[index], additionalInfo[index + 1]] = [
        additionalInfo[index + 1],
        additionalInfo[index],
      ];
    }

    setValue('additional_information', additionalInfo);
  }

  const linkError = errors.additional_information?.[index];

  return (
    <div className="flex flex-col-reverse xl:flex-row border border-gray-300 overflow-hidden rounded-md">
      <div className="flex flex-row xl:flex-col justify-end xl:justify-between space-x-2 xl:space-x-0 bg-gray-50 p-3">
        <button
          className="border border-gray-300 bg-white rounded-md p-2 disabled:opacity-40 hover:bg-gray-50"
          onClick={() => moveLink('up')}
          disabled={disabled || index === 0}
        >
          <ArrowUpIcon
            className="w-5 h-5 text-gray-500"
            strokeWidth={2}
            aria-hidden="true"
          />
        </button>
        <button
          className="border border-gray-300 bg-white rounded-md p-2 disabled:opacity-40 hover:bg-gray-50"
          onClick={() => moveLink('down')}
          disabled={disabled || isLast}
        >
          <ArrowDownIcon
            className="w-5 h-5 text-gray-500"
            strokeWidth={2}
            aria-hidden="true"
          />
        </button>
      </div>
      <div className="flex flex-col w-full py-4 px-6 space-y-3">
        <div className="flex flex-col xl:flex-row justify-between gap-4">
          <div className="flex-1">
            <FormInput
              type="text"
              label={`${
                contact.type === 'phone' ? 'Phone number label' : 'Email label'
              }`}
              value={contact.label || ''}
              {...register(`additional_information.${index}.label`)}
            />
            <div className="text-sm text-gray-500 mt-2">
              This will appear as a label above the{' '}
              {contact.type === 'phone' ? 'phone number' : 'email'}
            </div>
            {linkError && linkError.label && (
              <span className="text-sm text-red-400 font-medium">
                {linkError.label.message}
              </span>
            )}
          </div>
          <div className="flex-1">
            <FormInput
              label={`${contact.type === 'phone' ? 'Phone number' : 'Email'}`}
              type="text"
              value={contact.value}
              required
              {...register(`additional_information.${index}.value`)}
            />
            {linkError && linkError.value && (
              <span className="text-sm text-red-400 font-medium">
                {linkError.value.message}
              </span>
            )}
          </div>
        </div>
        <button
          className="text-brand-500 font-medium text-sm self-start"
          onClick={() => removeLink(index)}
        >
          Remove link
        </button>
      </div>
    </div>
  );
}
